import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API, money, fmtDate, serviceLabel, PAYMENT_METHODS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TopBar } from "../components/TopBar";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Download, Edit3, CheckCircle2, AlertTriangle, Clock,
  User, MapPin, Phone, Mail, Trash2, RotateCcw,
} from "lucide-react";

function StatusBadge({ inv }) {
  if (inv.status === "paid")
    return <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full bg-[#D1FAE5] text-[#059669]"><CheckCircle2 className="h-3.5 w-3.5" /> Paid</span>;
  if (inv.overdue)
    return <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full bg-[#FEE2E2] text-[#DC2626] ring-1 ring-[#DC2626]/30"><AlertTriangle className="h-3.5 w-3.5" /> Overdue</span>;
  return <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full bg-[#FEF3C7] text-[#B45309]"><Clock className="h-3.5 w-3.5" /> Unpaid</span>;
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between py-2 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-semibold text-slate-800 text-right">{value}</span>
    </div>
  );
}

export default function InvoiceDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token } = useAuth();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setInv(data);
    } catch {
      toast.error("Invoice not found");
      navigate("/");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const patchStatus = async (patch, msg) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/invoices/${id}/status`, patch);
      setInv(data);
      toast.success(msg);
    } catch {
      toast.error("Update failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      await api.post(`/invoices/${id}/pdf`); // generate & store latest
      const res = await api.get(`/invoices/${id}/pdf`, {
        params: { auth: token },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF generation failed");
    } finally {
      setDownloading(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Invoice deleted");
      navigate("/");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading || !inv) {
    return (
      <div className="min-h-screen bg-[#F8F6F0]">
        <TopBar />
        <div className="flex justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-[#1B3B2B]" /></div>
      </div>
    );
  }

  const c = inv.customer || {};
  const isPaid = inv.status === "paid";

  return (
    <div className="min-h-screen bg-[#F8F6F0]">
      <TopBar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-32">
        <button data-testid="back-button" onClick={() => navigate("/")}
          className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#1B3B2B] mb-4">
          <ArrowLeft className="h-4 w-4" /> Back to invoices
        </button>

        <div className="animate-fade-up space-y-4">
          {/* Header */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-mono-data text-sm font-semibold text-slate-400">{inv.invoice_number}</p>
                <h1 className="font-headline text-2xl font-bold text-slate-900 mt-0.5">{c.name}</h1>
              </div>
              <StatusBadge inv={inv} />
            </div>
            <div className="mt-4 flex items-end justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Total Due</p>
                <p data-testid="detail-total" className="font-mono-data font-extrabold text-3xl text-[#1B3B2B]">{money(inv.total)}</p>
              </div>
              <div className="text-right text-xs text-slate-500">
                <p>Invoice: {fmtDate(inv.invoice_date)}</p>
                <p>Service: {fmtDate(inv.service_date)}</p>
              </div>
            </div>
          </div>

          {/* Customer */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-2.5">
            <h2 className="font-headline font-semibold text-slate-800 text-sm flex items-center gap-2"><User className="h-4 w-4 text-[#1B3B2B]" /> Customer</h2>
            {c.phone && <p className="text-sm text-slate-600 flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-slate-400" /> {c.phone}</p>}
            {c.email && <p className="text-sm text-slate-600 flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-slate-400" /> {c.email}</p>}
            {c.billing_address && <p className="text-sm text-slate-600 flex items-start gap-2"><MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5" /> <span><span className="text-xs text-slate-400">Billing: </span>{c.billing_address}</span></p>}
            {c.service_address && c.service_address !== c.billing_address && (
              <p className="text-sm text-slate-600 flex items-start gap-2"><MapPin className="h-3.5 w-3.5 text-[#D97706] mt-0.5" /> <span><span className="text-xs text-[#D97706]">Service: </span>{c.service_address}</span></p>
            )}
          </div>

          {/* Line item */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="font-headline font-semibold text-slate-800 text-sm mb-3">Service</h2>
            <Row label="Service Type" value={serviceLabel(inv.service_type)} />
            {inv.description && <Row label="Description" value={inv.description} />}
            <Row label="Quantity" value={inv.quantity} />
            <Row label="Unit Price" value={money(inv.amount)} />
            <Row label="Subtotal" value={money(inv.subtotal)} />
            {inv.tax_enabled && <Row label={`Tax (${inv.tax_percentage}%)`} value={money(inv.tax_amount)} />}
            <div className="flex justify-between pt-3 mt-1 border-t-2 border-slate-100">
              <span className="font-semibold text-slate-900">Total</span>
              <span className="font-mono-data font-bold text-lg text-[#1B3B2B]">{money(inv.total)}</span>
            </div>
          </div>

          {/* Payment tracking */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
            <h2 className="font-headline font-semibold text-slate-800 text-sm">Payment Tracking</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5">Payment Method</p>
                <Select value={inv.payment_method || "none"} onValueChange={(v) => patchStatus({ payment_method: v === "none" ? "" : v }, "Payment method updated")}>
                  <SelectTrigger data-testid="detail-payment-method-select" className="h-11 border-slate-300">
                    <SelectValue placeholder="Not set" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not set</SelectItem>
                    {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2">
                {!inv.overdue && !isPaid ? (
                  <Button data-testid="mark-overdue-button" variant="outline" disabled={busy}
                    onClick={() => patchStatus({ overdue: true }, "Marked overdue")}
                    className="h-11 flex-1 border-[#DC2626]/30 text-[#DC2626] hover:bg-[#FEE2E2]">
                    <AlertTriangle className="h-4 w-4 mr-1.5" /> Overdue
                  </Button>
                ) : inv.overdue && !isPaid ? (
                  <Button data-testid="clear-overdue-button" variant="outline" disabled={busy}
                    onClick={() => patchStatus({ overdue: false }, "Overdue cleared")}
                    className="h-11 flex-1 border-slate-300 text-slate-600">
                    <RotateCcw className="h-4 w-4 mr-1.5" /> Clear Overdue
                  </Button>
                ) : null}
              </div>
            </div>
            {inv.notes && (
              <div className="text-sm text-slate-600 bg-[#F8F6F0] rounded-lg px-3 py-2.5">
                <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold block mb-1">Notes</span>
                {inv.notes}
              </div>
            )}
            <p className="text-xs text-slate-400">Last updated {fmtDate(inv.updated_at)}</p>
          </div>

          {/* Actions */}
          <div className="grid grid-cols-2 gap-3">
            {isPaid ? (
              <Button data-testid="mark-unpaid-button" disabled={busy} variant="outline"
                onClick={() => patchStatus({ status: "unpaid" }, "Marked unpaid")}
                className="h-12 border-slate-300 text-slate-700 font-semibold">
                <RotateCcw className="h-4 w-4 mr-1.5" /> Mark Unpaid
              </Button>
            ) : (
              <Button data-testid="mark-paid-button" disabled={busy}
                onClick={() => patchStatus({ status: "paid" }, "Marked paid")}
                className="h-12 bg-[#059669] hover:bg-[#047857] text-white font-semibold">
                <CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark Paid
              </Button>
            )}
            <Button data-testid="edit-invoice-button" variant="outline"
              onClick={() => navigate(`/invoice/${id}/edit`)}
              className="h-12 border-slate-300 text-slate-700 font-semibold">
              <Edit3 className="h-4 w-4 mr-1.5" /> Edit
            </Button>
          </div>

          <div className="flex items-center gap-3">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="delete-invoice-button" variant="ghost" className="h-11 text-slate-400 hover:text-[#DC2626] hover:bg-[#FEE2E2]">
                  <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this invoice?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {inv.invoice_number} for {c.name} will be permanently removed. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="confirm-delete-button" onClick={remove} className="bg-[#DC2626] hover:bg-[#b91c1c]">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </main>

      {/* Sticky PDF bar */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-3">
        <div className="max-w-3xl mx-auto">
          <Button data-testid="download-pdf-button" onClick={downloadPdf} disabled={downloading}
            className="w-full h-12 bg-[#D97706] hover:bg-[#b96205] text-white font-semibold rounded-lg text-base">
            {downloading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Download className="h-5 w-5 mr-2" /> Generate & Download PDF</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
